import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

let placesLibraryPromise;

// Google's dynamic loader. importLibrary("places") uses Places API (New).
// The legacy AutocompleteService endpoint is disabled for this project.
function ensureGoogleMapsBootstrap(apiKey) {
  const googleNamespace = window.google || (window.google = {});
  const maps = googleNamespace.maps || (googleNamespace.maps = {});
  if (typeof maps.importLibrary === "function") return;

  ((params) => {
    let loader;
    let script;
    let paramName;
    const c = "google";
    const importLibraryKey = "importLibrary";
    const callbackKey = "__ib__";
    const googleObj = window[c] || (window[c] = {});
    const mapsNamespace = googleObj.maps || (googleObj.maps = {});
    const libraries = new Set();
    const query = new URLSearchParams();
    const load = () =>
      loader ||
      (loader = new Promise((resolve, reject) => {
        script = document.createElement("script");
        query.set("libraries", [...libraries].join(","));
        for (paramName in params) {
          query.set(
            paramName.replace(/[A-Z]/g, (char) => `_${char[0].toLowerCase()}`),
            params[paramName]
          );
        }
        query.set("callback", `${c}.maps.${callbackKey}`);
        script.src = `https://maps.googleapis.com/maps/api/js?${query}`;
        script.async = true;
        mapsNamespace[callbackKey] = resolve;
        script.onerror = () =>
          reject(new Error("The Google Maps JavaScript API could not load."));
        const nonce = document.querySelector("script[nonce]")?.nonce;
        if (nonce) script.nonce = nonce;
        document.head.append(script);
      }));

    mapsNamespace[importLibraryKey] = (library, ...args) =>
      libraries.add(library) &&
      load().then(() => mapsNamespace[importLibraryKey](library, ...args));
  })({
    key: apiKey,
    v: "weekly",
  });
}

function loadPlacesLibrary(apiKey) {
  if (!apiKey) {
    return Promise.reject(new Error("Missing Google Maps API key"));
  }
  if (!placesLibraryPromise) {
    ensureGoogleMapsBootstrap(apiKey);
    placesLibraryPromise = window.google.maps
      .importLibrary("places")
      .catch((error) => {
        placesLibraryPromise = null;
        throw error;
      });
  }
  return placesLibraryPromise;
}

function matchedSubstrings(formattableText) {
  return (formattableText?.matches || []).map((match) => {
    const offset = match.startOffset ?? match.offset ?? 0;
    const length =
      match.endOffset != null ? match.endOffset - offset : match.length || 0;
    return { offset, length };
  });
}

function toAddressOption(prediction) {
  const description =
    prediction.text?.text || prediction.text?.toString?.() || "";
  return {
    description,
    placePrediction: prediction,
    structured_formatting: {
      main_text: prediction.mainText?.text || description,
      secondary_text: prediction.secondaryText?.text || "",
      main_text_matched_substrings: matchedSubstrings(prediction.mainText),
    },
  };
}

function coordinate(point, axis) {
  if (!point) return "";
  const value = point[axis];
  if (typeof value === "function") return value.call(point);
  return value ?? "";
}

function readAddressComponents(components = []) {
  const addressData = {
    postal_code: "",
    country: "",
    administrative_area_level_1: "",
    administrative_area_level_2: "",
    route: "",
  };
  components.forEach((item) => {
    (item.types || []).forEach((type) => {
      if (Object.prototype.hasOwnProperty.call(addressData, type)) {
        addressData[type] = item.longText || item.long_name || "";
      }
    });
  });
  return addressData;
}

export default function GoogleAddressSelect({
  address,
  setData,
  errors,
  required,
  label = "Address",
  readOnly = false,
}) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [placesReady, setPlacesReady] = useState(false);

  const placesRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestSeq = useRef(0);
  const detailsCache = useRef(new WeakMap());

  useEffect(() => {
    let active = true;
    loadPlacesLibrary(GOOGLE_MAPS_API_KEY)
      .then((places) => {
        if (!active) return;
        placesRef.current = places;
        sessionTokenRef.current = new places.AutocompleteSessionToken();
        setPlacesReady(true);
      })
      .catch((error) => {
        console.error("Failed to load Google Places", error);
      });
    return () => {
      active = false;
    };
  }, []);

  const fetchPredictions = useMemo(
    () =>
      debounce(async (input, requestId, callback) => {
        const places = placesRef.current;
        if (!places) {
          callback(requestId, []);
          return;
        }
        try {
          const { suggestions } =
            await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input,
              includedRegionCodes: ["us"],
              sessionToken: sessionTokenRef.current,
            });
          const results = (suggestions || [])
            .map((suggestion) => suggestion.placePrediction)
            .filter(Boolean)
            .map(toAddressOption);
          callback(requestId, results);
        } catch (error) {
          console.error("Place autocomplete failed", error);
          callback(requestId, []);
        }
      }, 400),
    []
  );

  useEffect(() => {
    let isActive = true;

    if (!placesReady || inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    const requestId = ++requestSeq.current;
    fetchPredictions(inputValue, requestId, (id, results) => {
      if (!isActive || id !== requestSeq.current) return;
      setOptions(value ? [value, ...(results || [])] : results || []);
    });

    return () => {
      isActive = false;
    };
  }, [inputValue, value, fetchPredictions, placesReady]);

  useEffect(() => {
    const prediction = value?.placePrediction;
    if (!prediction?.toPlace) return undefined;

    let active = true;
    let detailsPromise = detailsCache.current.get(prediction);
    if (!detailsPromise) {
      const place = prediction.toPlace();
      detailsPromise = place
        .fetchFields({
          fields: ["formattedAddress", "addressComponents", "location"],
        })
        .then((result) => result?.place || place);
      detailsCache.current.set(prediction, detailsPromise);
      if (placesRef.current?.AutocompleteSessionToken) {
        sessionTokenRef.current =
          new placesRef.current.AutocompleteSessionToken();
      }
    }

    detailsPromise
      .then((place) => {
        if (!active) return;
        const addressData = readAddressComponents(place.addressComponents);
        setData((prev) => ({
          ...prev,
          completeAddress: place.formattedAddress || "",
          addressLocality: addressData.administrative_area_level_2,
          addressRegion: addressData.administrative_area_level_1,
          streetAddress: addressData.route,
          postalCode: addressData.postal_code,
          addressCountry: addressData.country,
          latitude: coordinate(place.location, "lat"),
          longitude: coordinate(place.location, "lng"),
        }));
      })
      .catch((error) => {
        if (!active) return;
        console.error("Place details failed", error);
      });

    return () => {
      active = false;
    };
  }, [setData, value]);

  useEffect(() => {
    if (address) {
      const lastCommaIndex = address.indexOf(",");
      const initialAddress = {
        description: address,
        structured_formatting: {
          main_text: address.substring(0, lastCommaIndex).trim(),
          secondary_text: address.substring(lastCommaIndex + 1).trim(),
        },
      };

      setValue(initialAddress);
      setInputValue(initialAddress.description || initialAddress);
    }
  }, [address]);

  return (
    <Autocomplete
      fullWidth
      className="google-address-select"
      options={options}
      value={value}
      inputValue={inputValue}
      disabled={readOnly}
      onChange={(event, newValue) => {
        setValue(newValue);
        if (!newValue) {
          setInputValue("");
          setData((prev) => ({
            ...prev,
            completeAddress: "",
            addressLocality: "",
            addressRegion: "",
            streetAddress: "",
            postalCode: "",
            addressCountry: "",
            latitude: "",
            longitude: "",
          }));
        }
      }}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      noOptionsText="No locations"
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          fullWidth
          error={errors?.completeAddress}
          helperText={errors?.completeAddress}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const matches =
          option?.structured_formatting?.main_text_matched_substrings || [];
        const parts = parse(
          option?.structured_formatting?.main_text || "",
          matches.map((match) => [match.offset, match.offset + match.length])
        );
        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {option.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
